import { GoogleOutlined } from "@ant-design/icons";
import {
  CloudflareIconUrl,
  PingIconUrl,
  integrationRegistry,
} from "@p0security/shared/integrations/registry";
import type { OrgData } from "@p0security/shared/types/tenant/index";
import type {
  AzureOIDCProvider,
  GoogleOIDCProvider,
  LegacyOktaSsoProvider,
  MicrosoftSsoProvider,
  OidcPkceProvider,
} from "@p0security/shared/types/tenant/sso";
import type {
  Auth,
  AuthProvider,
  NextOrObserver,
  OAuthCredential,
  Unsubscribe,
  User,
  UserCredential,
} from "firebase/auth";
import {
  GoogleAuthProvider,
  OAuthProvider,
  createUserWithEmailAndPassword,
  getAuth,
  isSignInWithEmailLink,
  sendEmailVerification,
  sendPasswordResetEmail,
  sendSignInLinkToEmail,
  signInWithEmailAndPassword,
  signInWithEmailLink,
  signInWithPopup,
} from "firebase/auth";
import type React from "react";

import { auth } from "../../providers/FirestoreProvider";
import { LimitedUse } from "../Integrations/Google/LimitedUse";
import { Logo } from "../Integrations/Logo";
import { signInWithPkce } from "./pkce";

const verifyEmailCallbackUrl = `${window.location.origin}/login/email-verified?orgSlug=[orgSlug]`;
const changePasswordCallbackUrl = `${window.location.origin}/login/change-password?orgSlug=[orgSlug]`;

const actionCodeSettingsBase = {
  url: `${window.location.origin}/login/email-callback?orgSlug=[orgSlug]`,
  handleCodeInApp: true,
};

export namespace idPlatform {
  export async function finishEmailSignIn(
    email: string,
    tenantId: string
  ): Promise<UserCredential | undefined> {
    auth.tenantId = tenantId;
    if (isSignInWithEmailLink(auth, window.location.href)) {
      return await signInWithEmailLink(auth, email, window.location.href);
    }
  }

  export async function signIn(
    tenantDetails: OrgData,
    options: {
      email?: string;
      password?: string;
      orgSlug: string;
    }
  ): Promise<OAuthCredential | UserCredential | null | void> {
    const idp = idpSelector(tenantDetails);
    if (tenantDetails.auth?.type === "sso" && idp === undefined) {
      throw new Error(
        "Single sign-on isn't configured correctly for this organization. Contact your P0 administrator."
      );
    }
    auth.tenantId = tenantDetails.tenantId;
    // `signInWithPopup` below is the actual login window managed by Google Identity Platform and the configured Provider
    // See an example for OIDC: https://cloud.google.com/identity-platform/docs/web/oidc#signing_in_users_with_oauth
    if (
      idp?.flow === "pkce" &&
      tenantDetails.auth.type === "sso" &&
      tenantDetails.auth.provider.ssoProvider === "oidc-pkce"
    ) {
      const provider = idp.authProvider();
      const result = await signInWithPkce(
        auth,
        provider as OAuthProvider,
        tenantDetails.auth.provider,
        options.orgSlug
      );
      return idp.credentialFromResult(result);
    } else if (idp !== undefined) {
      const result = await signInWithPopup(auth, idp.authProvider());
      return idp.credentialFromResult(result);
    } else if (options.password !== undefined && options.email !== undefined) {
      const actionCodeSettings = { ...actionCodeSettingsBase };
      actionCodeSettings.url = actionCodeSettings.url.replace(
        "[orgSlug]",
        options.orgSlug
      );
      return signInWithEmailAndPassword(
        auth,
        options.email,
        options.password
      ).catch((error) => alert(error.message));
    } else if (options.email !== undefined) {
      const actionCodeSettings = { ...actionCodeSettingsBase };
      actionCodeSettings.url = actionCodeSettings.url.replace(
        "[orgSlug]",
        options.orgSlug
      );
      return sendSignInLinkToEmail(
        auth,
        options.email,
        actionCodeSettings
      ).catch((error) => alert(error.message));
    }
  }

  const createVerifyEmailActionCodeSettings = async ({
    orgSlug,
    tenantId,
    url,
  }: {
    orgSlug: string;
    tenantId: string;
    url: string;
  }): Promise<{
    auth: Auth;
    actionCodeSettings: { url: string; handleCodeInApp: boolean };
  }> => {
    // TODO: only email sign up is supported for now.
    const auth: Auth = getAuth();
    auth.tenantId = tenantId;
    const actionCodeSettings = {
      handleCodeInApp: true,
      url: url.replace("[orgSlug]", orgSlug),
    };
    return { auth, actionCodeSettings };
  };

  export async function signUp(
    tenantDetails: OrgData,
    options: {
      email: string;
      password: string;
      orgSlug: string;
    }
  ) {
    // TODO: only email sign up is supported for now.
    const { auth, actionCodeSettings } =
      await createVerifyEmailActionCodeSettings({
        orgSlug: options.orgSlug,
        tenantId: tenantDetails.tenantId,
        url: verifyEmailCallbackUrl,
      });
    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        options.email,
        options.password
      );

      return await sendEmailVerification(
        userCredential.user,
        actionCodeSettings
      );
    } catch (error) {
      alert(error);
    }
  }

  export async function resendEmailVerification(
    tenantDetails: OrgData,
    options: {
      user: User;
      orgSlug: string;
    }
  ) {
    const { actionCodeSettings } = await createVerifyEmailActionCodeSettings({
      orgSlug: options.orgSlug,
      tenantId: tenantDetails.tenantId,
      url: verifyEmailCallbackUrl,
    });
    try {
      return await sendEmailVerification(options.user, actionCodeSettings);
    } catch (error) {
      alert(error);
    }
  }

  export async function sendPasswordReset(
    tenantDetails: OrgData,
    options: {
      email: string;
      orgSlug: string;
    }
  ) {
    const { auth, actionCodeSettings } =
      await createVerifyEmailActionCodeSettings({
        orgSlug: options.orgSlug,
        tenantId: tenantDetails.tenantId,
        url: changePasswordCallbackUrl,
      });
    try {
      return await sendPasswordResetEmail(
        auth,
        options.email,
        actionCodeSettings
      );
    } catch (error) {
      alert(error);
    }
  }

  export async function signOut() {
    return await getAuth().signOut(); // Requires initialized Firestore
  }

  export function onAuthStateChanged(
    nextOrObserver: NextOrObserver<User>
  ): Unsubscribe {
    return getAuth().onAuthStateChanged(nextOrObserver); // Requires initialized Firestore
  }

  export function element(tenantDetails: OrgData) {
    const idp = idpSelector(tenantDetails);
    if (idp !== undefined) return idp.component();
    if (tenantDetails.auth?.type === "sso") {
      return (
        <span>
          Single sign-on isn&apos;t configured correctly for this organization.
          Contact your P0 administrator.
        </span>
      );
    }
    return false;
  }

  export function footer(tenantDetails: OrgData) {
    const idp = idpSelector(tenantDetails);
    return idp?.footer;
  }
}

type IdentityProvider = {
  component: () => React.ReactElement;
  authProvider: () => AuthProvider;
  credentialFromResult: (result: UserCredential) => OAuthCredential | null;
  flow?: "pkce" | "popup";
  footer?: React.ReactElement;
};

const idpSelector = (tenant: OrgData): IdentityProvider | undefined => {
  if (tenant.auth?.type !== "sso") {
    return undefined;
  }

  const { ssoProvider } = tenant.auth.provider;
  switch (ssoProvider) {
    case undefined:
      return undefined;
    case "google":
      return googleIdentityProvider();
    case "microsoft":
      return microsoftIdentityProvider(tenant.auth.provider);
    case "okta":
      return oktaIdentityProvider(tenant.auth.provider);
    case "oidc-pkce":
      return pkceIdentityProvider(tenant.auth.provider);
    case "google-oidc":
      return googleOidcIdentityProvider(tenant.auth.provider);
    case "azure-oidc":
      return azureOidcIdentityProvider(tenant.auth.provider);
    default: {
      const unhandled: never = ssoProvider;
      void unhandled;
      return undefined;
    }
  }
};

const googleIdentityProvider = (): IdentityProvider => ({
  component: () => {
    return (
      <>
        <GoogleOutlined /> Sign in with Google
      </>
    );
  },
  authProvider: () => new GoogleAuthProvider(),
  credentialFromResult: (result: UserCredential) =>
    GoogleAuthProvider.credentialFromResult(result),
  footer: <LimitedUse />,
});

const googleOidcIdentityProvider = (
  provider: GoogleOIDCProvider
): IdentityProvider => ({
  component: () => {
    return (
      <>
        <GoogleOutlined /> Sign in with Google
      </>
    );
  },
  authProvider: () => {
    return new OAuthProvider(`oidc.${provider.ssoProvider}`);
  },
  credentialFromResult: (result: UserCredential) => {
    return OAuthProvider.credentialFromResult(result);
  },
  footer: <LimitedUse />,
});

const azureOidcIdentityProvider = (
  provider: AzureOIDCProvider
): IdentityProvider => ({
  component: () => {
    return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        <img
          src={integrationRegistry["entra-id"].logoUrl}
          width={15}
          alt="Entra ID"
        />
        Sign in with Microsoft | Entra ID
      </span>
    );
  },
  authProvider: () => {
    return new OAuthProvider(
      `oidc.${provider.ssoProvider}`
    ).setCustomParameters({
      tenant: provider.microsoftPrimaryDomain,
    });
  },
  credentialFromResult: (result: UserCredential) => {
    return OAuthProvider.credentialFromResult(result);
  },
});

const microsoftIdentityProvider = (
  provider: MicrosoftSsoProvider
): IdentityProvider => ({
  component: () => {
    return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        <img
          src={integrationRegistry["entra-id"].logoUrl}
          width={15}
          alt="Entra ID"
        />
        Sign in with Microsoft | Entra ID
      </span>
    );
  },
  authProvider: () => {
    // Prevent another Microsoft tenant or a personal account from signing in to this tenant
    // If this is not set, a random live.com account can log in to any Azure AD tenant,
    // albeit, they won't see any data because of the Firestore rules that enforce a regex match
    // on the domain using the Firestore document field `o/{tenantId}.openIdDomain`.
    // Make sure to update that field and the `orgs/{tenantSlug}.microsoftPrimaryDomain` field simultaneously.
    // If this field is set, the login attempt is blocked with the error message "User account 'user@hotmail.com'
    // from identity provider 'live.com' does not exist in tenant 'XYZ'...".
    return new OAuthProvider("microsoft.com").setCustomParameters({
      tenant: provider.microsoftPrimaryDomain,
    });
  },
  credentialFromResult: (result: UserCredential) => {
    return OAuthProvider.credentialFromResult(result);
  },
});

const pkceIdentityProvider = (
  provider: OidcPkceProvider
): IdentityProvider | undefined => {
  const providerType = provider.providerType;
  const identityProviderDetails = (() => {
    switch (providerType) {
      case "okta":
        return [integrationRegistry.okta.logoUrl, "Okta"] as const;
      case "cloudflare":
        return [CloudflareIconUrl, "Cloudflare"] as const;
      case "ping":
        return [PingIconUrl, "Ping Identity"] as const;
      default: {
        const unhandled: never = providerType;
        void unhandled;
        return undefined;
      }
    }
  })();
  if (identityProviderDetails === undefined) return undefined;
  const [logoUrl, title] = identityProviderDetails;
  return {
    component: () => (
      <>
        <Logo logo={logoUrl} title={title} /> Sign in with {title}
      </>
    ),
    flow: "pkce",
    authProvider: () => new OAuthProvider(provider.providerId),
    credentialFromResult: OAuthProvider.credentialFromResult,
  };
};

/** @deprecated New users should use pkceIdentityProvider instead
 *
 * This is for backwards compatibility for customers that do not yet have a PKCE-requiring native OIDC app.
 */
const oktaIdentityProvider = (
  provider: LegacyOktaSsoProvider
): IdentityProvider => ({
  component: () => {
    return (
      <>
        <Logo logo={integrationRegistry.okta.logoUrl} title="Okta" /> Sign in
        with Okta
      </>
    );
  },
  authProvider: () => {
    // See https://cloud.google.com/identity-platform/docs/web/oidc?_ga=2.24136511.-468889233.1666576248#signing_in_users_with_oauth
    // The provider ID is tenant-specific
    return new OAuthProvider(provider.providerId);
  },
  credentialFromResult: (result: UserCredential) => {
    return OAuthProvider.credentialFromResult(result);
  },
});
